import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { projectApi } from '../../../services/api';
import { isManager } from '../../../utils/roleUtils';
import { TextField } from '../../ui/TextField';
import '../Dashboard.css';

const FILTER_THRESHOLD = 8;

const AccessibleProjectsShortcut = ({ userRole }) => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    let cancelled = false;

    const loadProjects = async () => {
      try {
        const data = await projectApi.getAccessibleProjects();
        if (!cancelled) {
          setProjects(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error('Failed to load accessible projects:', error);
        if (!cancelled) {
          setProjects([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadProjects();
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredProjects = useMemo(() => {
    const query = filter.trim().toLowerCase();
    if (!query) return projects;
    return projects.filter((project) => {
      const name = (project.name || '').toLowerCase();
      const abbreviation = (project.abbreviation || '').toLowerCase();
      return name.includes(query) || abbreviation.includes(query);
    });
  }, [filter, projects]);

  const handleProjectClick = (projectId) => {
    if (!userRole) return;
    if (isManager(userRole)) {
      navigate(`/projects/${projectId}`);
      return;
    }
    navigate(`/my-projects/${projectId}/notes-artifacts`);
  };

  if (loading) {
    return null;
  }

  return (
    <section className="accessible-projects-shortcut" aria-label="Active projects">
      <div className="accessible-projects-header">
        <div>
          <h2 className="accessible-projects-title">Active projects</h2>
          <p className="accessible-projects-subtitle">Jump straight to a project you can access</p>
        </div>
        {projects.length > 0 && (
          <span className="accessible-projects-count">{projects.length}</span>
        )}
      </div>

      {projects.length > FILTER_THRESHOLD && (
        <TextField className="accessible-projects-filter" label="Filter projects">
          <TextField.Input
            type="search"
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            placeholder="Search by name or abbreviation"
          />
        </TextField>
      )}

      {projects.length === 0 ? (
        <p className="accessible-projects-empty">No active projects</p>
      ) : filteredProjects.length === 0 ? (
        <p className="accessible-projects-empty">No matching active projects</p>
      ) : (
        <ul className="accessible-projects-list">
          {filteredProjects.map((project) => (
            <li key={project.id}>
              <button
                type="button"
                className="accessible-project-item"
                onClick={() => handleProjectClick(project.id)}
                disabled={!userRole}
              >
                <span className="accessible-project-name">{project.name}</span>
                {project.abbreviation && (
                  <span className="accessible-project-abbr">{project.abbreviation}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default AccessibleProjectsShortcut;
